import { makeObservable, observable, action, runInAction, computed } from 'mobx';
import {
  searchScenarios,
  getScenario,
  getScenarioRunHistory,
  listScenarioCategories,
  type ScenarioSummary,
  type ScenarioDetail,
  type ScenarioRunHistoryItem,
  type ScenarioSearchParams,
  type ScenarioCategoryDto,
} from '@/api/scenarios';
import { getRunJob, isJobUnfinished } from '@/api/validationRuns';
import type { RootStore } from './RootStore';

// ─────────────────────────────────────────────────────────────────────────────
// ScenariosStore — drives the Scenario Library page.
//
// FILTER MODEL (Phase 2.A.5)
//   Server-side filters live on `params` and trigger a re-fetch when changed:
//     agentId, audience, categories[], turnCount, hasSkillAssertions,
//     hasBannedPhrases, tags[].
//   Client-side filter is the free-text search box — applied on the
//   already-fetched page so typing stays instant.
//
//   Why hybrid? Server-side filters narrow the working set so the UI never
//   has to think about "all 5,000 scenarios in the merchant" client-side,
//   but the search box wants to feel snappy without round-tripping.
//
// FETCH STRATEGY
//   Every server-filter setter calls `fetchDebounced()`. The debouncer
//   coalesces rapid setter calls (e.g. selecting 3 categories at once)
//   into one request. The free-text search does NOT trigger a fetch.
// ─────────────────────────────────────────────────────────────────────────────

// localStorage key for the sticky agent filter on the Scenario library page.
// Per-page key — keep distinct from Recent runs so each page can remember
// its own "last where I left off."
const AGENT_FILTER_STORAGE_KEY = 'agentstudio.scenarios.lastAgentId';

function loadStoredAgentFilter(): number | undefined {
  try {
    const raw = window.localStorage.getItem(AGENT_FILTER_STORAGE_KEY);
    return raw ? Number(raw) : undefined;
  } catch {
    return undefined;
  }
}

function saveStoredAgentFilter(v: number | undefined): void {
  try {
    if (v == null) window.localStorage.removeItem(AGENT_FILTER_STORAGE_KEY);
    else window.localStorage.setItem(AGENT_FILTER_STORAGE_KEY, String(v));
  } catch {
    // localStorage can throw in private mode / quota exceeded — non-fatal.
  }
}

export class ScenariosStore {
  @observable items: ScenarioSummary[] = [];
  @observable categoryCatalog: ScenarioCategoryDto[] = [];
  @observable isLoading = false;
  @observable error: string | null = null;

  // Client-side filter (cheap, no refetch).
  @observable search = '';

  // Server-side filter shape — drives /api/Scenarios/Search.
  // We keep a single object (rather than scattered fields) so adding the
  // next filter in v1.7 is one line, not three.
  // Hydrated from localStorage so the agent picker is sticky across
  // browser sessions (small UX win the user explicitly asked for).
  @observable params: ScenarioSearchParams = { agentId: loadStoredAgentFilter() };

  // Detail (open at most one at a time).
  @observable openDetail: ScenarioDetail | null = null;
  @observable isLoadingDetail = false;
  @observable detailError: string | null = null;

  // Run history for the open scenario.
  @observable runHistory: ScenarioRunHistoryItem[] = [];
  @observable isLoadingHistory = false;

  // ── In-flight run tracking (drawer-scoped) ─────────────────────────────
  // When the user clicks Run on the open scenario, we set
  // `pendingRunStartedAt` and start polling Run history. The drawer uses
  // this to:
  //   * keep the Run button in "Running…" state with elapsed time
  //   * insert a "Running now…" placeholder row at the top of Run history
  //   * auto-switch the active tab to Run history so the nurse sees
  //     where the result will land (no manual refresh required)
  // Polling stops when a new run row newer than pendingRunStartedAt
  // appears in runHistory, OR after PendingRunMaxWaitMs (5 min) at which
  // point we surface "this is taking longer than expected" guidance.
  @observable pendingRunStartedAt: Date | null = null;
  @observable pendingRunCorrelationId: string | null = null;
  /** Review R7: the durable job this drawer's Run started. Polling follows IT, not any newer result. */
  @observable pendingRunJobId: number | null = null;
  /** The last run request ended without a result to show (refused, failed, cancelled), and why. */
  @observable lastRunProblem: string | null = null;
  private static readonly PendingJobMaxWaitMs = 60 * 60_000;
  private _pollTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly PollIntervalMs = 8_000;
  private static readonly PendingRunMaxWaitMs = 5 * 60_000;

  private _fetchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(_root: RootStore) {
    void _root;
    makeObservable(this);
  }

  // ── Filter setters ──────────────────────────────────────────────────────

  @action setSearch(v: string): void { this.search = v; }

  @action setAgentFilter(agentId: number | null): void {
    this.params = { ...this.params, agentId: agentId ?? undefined };
    saveStoredAgentFilter(agentId ?? undefined);
    this.fetchDebounced();
  }

  @action setAudienceFilter(audience: string | null): void {
    this.params = { ...this.params, audience: audience ?? undefined };
    this.fetchDebounced();
  }

  @action setCategoriesFilter(codes: string[]): void {
    this.params = {
      ...this.params,
      categories: codes.length > 0 ? codes.join(',') : undefined,
    };
    this.fetchDebounced();
  }

  @action setTurnCountFilter(mode: 'multi' | 'single' | null): void {
    this.params = { ...this.params, turnCount: mode ?? undefined };
    this.fetchDebounced();
  }

  @action setTagsFilter(tagList: string[]): void {
    this.params = {
      ...this.params,
      tags: tagList.length > 0 ? tagList.join(',') : undefined,
    };
    this.fetchDebounced();
  }

  @action setHasSkillAssertions(v: boolean | null): void {
    this.params = { ...this.params, hasSkillAssertions: v ?? undefined };
    this.fetchDebounced();
  }

  @action setHasBannedPhrases(v: boolean | null): void {
    this.params = { ...this.params, hasBannedPhrases: v ?? undefined };
    this.fetchDebounced();
  }

  @action
  clearFilters(): void {
    this.search = '';
    this.params = {};
    saveStoredAgentFilter(undefined);
    this.fetchDebounced();
  }

  // ── Fetch ───────────────────────────────────────────────────────────────

  /**
   * Coalesce rapid filter changes into one server call. 120ms feels
   * imperceptible for filter pills but is enough to merge a multi-click
   * spree (e.g. selecting 3 categories quickly).
   */
  fetchDebounced(): void {
    if (this._fetchTimer) clearTimeout(this._fetchTimer);
    this._fetchTimer = setTimeout(() => {
      this._fetchTimer = null;
      void this.fetch();
    }, 120);
  }

  @action
  async fetch(): Promise<void> {
    this.isLoading = true;
    this.error = null;
    try {
      const items = await searchScenarios(this.params);
      runInAction(() => {
        this.items = items;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load scenarios';
      });
    }
  }

  /**
   * Pull the per-domain category taxonomy once per session. The filter
   * bar's "Category" dropdown reads from this — the labels and ordering
   * come straight from the DB so a vertical rename is a data change,
   * not a UI deploy.
   */
  @action
  async fetchCategoryCatalog(domain?: string): Promise<void> {
    try {
      const rows = await listScenarioCategories(domain);
      runInAction(() => {
        this.categoryCatalog = rows;
      });
    } catch {
      // Non-fatal — the filter bar falls back to raw codes when the
      // catalog is empty.
      runInAction(() => {
        this.categoryCatalog = [];
      });
    }
  }

  // ── Derived views ───────────────────────────────────────────────────────

  @computed get filtered(): ScenarioSummary[] {
    const term = this.search.trim().toLowerCase();
    if (!term) return this.items;
    return this.items.filter((s) => {
      const blob = [
        s.scenarioId,
        s.description ?? '',
        s.fileName,
        s.agentCanonicalName,
        s.agentDisplayName ?? '',
        s.tags ?? '',
      ].join(' ').toLowerCase();
      return blob.includes(term);
    });
  }

  /**
   * Grouped agent labels for any UI that wants to surface the selected
   * agent as a chip. Most pages use AgentPickerTrigger directly now; this
   * is kept for any callers that still want the in-store list.
   */
  @computed get agentCanonicalNames(): { canonicalName: string; displayName?: string; count: number; agentId: number }[] {
    const map = new Map<number, { canonicalName: string; displayName?: string; count: number }>();
    for (const s of this.items) {
      const existing = map.get(s.agentId);
      if (existing) existing.count++;
      else {
        map.set(s.agentId, {
          canonicalName: s.agentCanonicalName,
          displayName: s.agentDisplayName,
          count: 1,
        });
      }
    }
    return [...map.entries()]
      .map(([agentId, v]) => ({ agentId, ...v }))
      .sort((a, b) => a.canonicalName.localeCompare(b.canonicalName));
  }

  /** Distinct audiences present in the current result set — populates the audience pill row. */
  @computed get audiences(): string[] {
    const set = new Set<string>();
    for (const s of this.items) {
      if (s.audience) set.add(s.audience);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }

  @computed get totalCount(): number {
    return this.items.length;
  }

  @computed get filteredCount(): number {
    return this.filtered.length;
  }

  /** Number of server-side filters currently applied (for the "Clear filters" button visibility). */
  @computed get activeFilterCount(): number {
    let n = 0;
    if (this.params.agentId != null) n++;
    if (this.params.audience) n++;
    if (this.params.categories) n++;
    if (this.params.turnCount) n++;
    if (this.params.tags) n++;
    if (this.params.hasSkillAssertions != null) n++;
    if (this.params.hasBannedPhrases != null) n++;
    return n;
  }

  // ── Detail ──────────────────────────────────────────────────────────────

  @action
  async openByPath(filePath: string): Promise<void> {
    this.isLoadingDetail = true;
    this.detailError = null;
    this.openDetail = null;
    this.runHistory = [];
    try {
      const detail = await getScenario(filePath);
      runInAction(() => {
        this.openDetail = detail;
        this.isLoadingDetail = false;
      });
      void this.fetchHistory(detail.summary.scenarioId, detail.summary.agentId);
    } catch (err) {
      runInAction(() => {
        this.isLoadingDetail = false;
        // Plain-language message for non-technical readers. The
        // underlying error stays in the console for engineers.
        this.detailError =
          'We could not load this test. Try again, or contact support if it keeps happening.';
      });
      // eslint-disable-next-line no-console
      console.error('[ScenariosStore.openByPath] failed:', err);
    }
  }

  @action
  closeDetail(): void {
    this.openDetail = null;
    this.detailError = null;
    this.runHistory = [];
    this.lastRunProblem = null;
    this.stopPollingForNewRun();
  }

  @action
  private async fetchHistory(scenarioId: string, agentId: number): Promise<void> {
    this.isLoadingHistory = true;
    try {
      const history = await getScenarioRunHistory(scenarioId, agentId, 30);
      runInAction(() => {
        this.runHistory = history;
        this.isLoadingHistory = false;
      });
    } catch {
      runInAction(() => {
        this.runHistory = [];
        this.isLoadingHistory = false;
      });
    }
  }

  // ── Pending-run polling ─────────────────────────────────────────────────
  //
  // After a user clicks Run, we kick off a polling loop that fetches the
  // run history every 8 seconds. The first row whose `runDate` is at
  // or after the click time is the user's run — at that point we stop
  // polling and the UI auto-updates.
  //
  // PendingRunMaxWaitMs (5 min) is the upper bound for "in flight" — past
  // that we stop polling, leave the pending state on, and the UI surfaces
  // a "this is taking longer than expected" hint.

  @action
  markRunPending(correlationId: string, jobId?: number | null): void {
    this.pendingRunStartedAt = new Date();
    this.pendingRunCorrelationId = correlationId;
    this.pendingRunJobId = jobId ?? null;
    this.lastRunProblem = null;
  }

  @action
  startPollingForNewRun(scenarioId: string, agentId: number): void {
    const jobId = this.pendingRunJobId;
    const startedAt = this.pendingRunStartedAt ?? new Date();
    if (this._pollTimer) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
    if (this.pendingRunStartedAt == null) this.pendingRunStartedAt = startedAt;

    // Review R7: with a job id, follow that job until it ends. Its own run is what lands, and a request that ends
    // without a result (refused, failed, cancelled) says so instead of staying "Running" until a timeout.
    if (jobId != null) {
      const followJob = async () => {
        try {
          const job = await getRunJob(jobId);
          if (isJobUnfinished(job.state)) {
            if (Date.now() - startedAt.getTime() > ScenariosStore.PendingJobMaxWaitMs) {
              this._pollTimer = null;
              return;
            }
            this._pollTimer = setTimeout(followJob, ScenariosStore.PollIntervalMs);
            return;
          }
          const history = await getScenarioRunHistory(scenarioId, agentId, 5);
          runInAction(() => {
            this.runHistory = history;
            this.lastRunProblem = job.validationRunId
              ? job.state === 'Cancelled'
                ? 'The run was cancelled; the results of the scenarios that ran are kept.'
                : null
              : job.refusalReason ?? job.failureReason ?? `The run request ended ${job.state} without a result.`;
            this.pendingRunStartedAt = null;
            this.pendingRunCorrelationId = null;
            this.pendingRunJobId = null;
          });
          this._pollTimer = null;
        } catch {
          this._pollTimer = setTimeout(followJob, ScenariosStore.PollIntervalMs);
        }
      };
      this._pollTimer = setTimeout(followJob, ScenariosStore.PollIntervalMs);
      return;
    }

    const tick = async () => {
      try {
        const history = await getScenarioRunHistory(scenarioId, agentId, 5);
        runInAction(() => {
          this.runHistory = history;
        });

        // Did a new run land since we started?
        const newest = history[0];
        if (newest && new Date(newest.runDate) >= startedAt) {
          runInAction(() => {
            this.pendingRunStartedAt = null;
            this.pendingRunCorrelationId = null;
          });
          this._pollTimer = null;
          return;
        }

        // Past the max wait? Stop polling but keep the pending flag so
        // the UI shows "taking longer than expected."
        const elapsed = Date.now() - startedAt.getTime();
        if (elapsed > ScenariosStore.PendingRunMaxWaitMs) {
          this._pollTimer = null;
          return;
        }

        // Schedule next tick.
        this._pollTimer = setTimeout(tick, ScenariosStore.PollIntervalMs);
      } catch {
        // Network blip — try again next tick rather than failing loudly.
        this._pollTimer = setTimeout(tick, ScenariosStore.PollIntervalMs);
      }
    };

    // First tick after PollIntervalMs (give the listener time to start).
    this._pollTimer = setTimeout(tick, ScenariosStore.PollIntervalMs);
  }

  @action
  stopPollingForNewRun(): void {
    if (this._pollTimer) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
    this.pendingRunStartedAt = null;
    this.pendingRunCorrelationId = null;
    this.pendingRunJobId = null;
  }

  /** Elapsed seconds since the user clicked Run, or null if no run is pending. */
  @computed get pendingRunElapsedSeconds(): number | null {
    if (this.pendingRunStartedAt == null) return null;
    return Math.floor((Date.now() - this.pendingRunStartedAt.getTime()) / 1000);
  }
}
